import { useI18n } from '../../../i18n';
// ============================================
// StoreStatsRow Component
// 3 KPI stat cards: Total Stores, Warehouses, Capacity Utilization
// ============================================

import { StoresIcon, WarehouseIcon } from '../../../components/icons';
import { cn } from '../../../utils/cn';

interface StoreStatsRowProps {
  totalStores: number;
  totalStoresChange?: number;
  warehouses?: number;
  capacityUtilization?: number;
  isLoading?: boolean;
  className?: string;
}

export function StoreStatsRow({
  totalStores,
  totalStoresChange = 0,
  warehouses,
  capacityUtilization,
  isLoading,
  className,
}: StoreStatsRowProps) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className={cn('grid grid-cols-2 gap-4 md:grid-cols-3', className)}>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[100px] animate-pulse rounded-xl bg-white shadow-sm"
            style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
          />
        ))}
      </div>
    );
  }

  const isNearLimit = capacityUtilization != null && capacityUtilization >= 85;

  return (
    <div className={cn('grid grid-cols-2 gap-4 md:grid-cols-3', className)}>
      {/* Total Stores Card */}
      <div
        className="flex items-start justify-between rounded-xl bg-white p-3 shadow-sm"
        style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
      >
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">{t('totalStores')}</p>
          <div className="flex items-baseline gap-2">
            <p className="font-sans text-xl font-bold leading-none text-[#014361]">
              {totalStores}
            </p>
            {totalStoresChange !== 0 && (
              <span className="text-xs font-medium">
                <span className={totalStoresChange > 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}>
                  {totalStoresChange > 0 ? `+${totalStoresChange}` : totalStoresChange} stores
                </span>
                <span className="text-slate-900"> {t('inThisMonth')}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
          <StoresIcon size={24} className="text-[#0288D1]" />
        </div>
      </div>

      {/* Warehouses Card */}
      <div
        className="flex items-start justify-between rounded-xl bg-white p-3 shadow-sm"
        style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
      >
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">{t('warehouses')}</p>
          <p className="font-sans text-xl font-bold leading-none text-[#014361]">
            {warehouses ?? 'N/A'}
          </p>
        </div>
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EDF7ED]">
          <WarehouseIcon size={24} className="text-[#219653]" />
        </div>
      </div>

      {/* Capacity Utilization Card */}
      <div
        className="col-span-2 flex items-start justify-between rounded-xl bg-white p-3 shadow-sm md:col-span-1"
        style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
      >
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">{t('capacityUtilization')}</p>
          <div className="flex items-baseline gap-2">
            <p className={cn('font-sans text-xl font-bold leading-none', isNearLimit ? 'text-[#EF4444]' : 'text-[#014361]')}>
              {capacityUtilization != null ? `${capacityUtilization}%` : 'N/A'}
            </p>
            {isNearLimit && (
              <span className="text-xs font-semibold text-[#EF4444]">{t('nearLimit')}</span>
            )}
          </div>
        </div>
        <div className={cn('flex h-12 w-12 items-center justify-center rounded-xl', isNearLimit ? 'bg-red-50' : 'bg-slate-100')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={isNearLimit ? 'text-[#EF4444]' : 'text-slate-500'}>
            <path d="M12 2L2 19h20L12 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none"/>
            <path d="M12 9v5M12 16.5v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
}
